import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import cn from 'classnames';
import { Design, HotTopic, ShopCollection } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { fetchHotTopics } from '@/utils/http/fetchHotTopics';
import { fetchShopCollections } from '@/utils/http/fetchShopCollections';
import { t } from '@/utils/i18n';
import { isCYOMode, isEmbroideryMode, isUserAreaMode } from '@/utils/params';
import { trackWellness } from '@/utils/wellnes';
import { useIsSmall } from '@/hooks/useMediaQuery';
import { getHasScreenPrintingPrintType } from '@/model/printType/getHasScreenPrintingPrintType';
import { getPrintTypesForPrintArea } from '@/model/printType/getPrintTypesForPrintArea';
import { isPrintColorColorSpace } from '@/model/printType/isPrintColorColorSpace';
import { addDesignConfigurationAction } from '@/redux/configurations/actions/addDesignConfigurationAction';
import { getContext } from '@/redux/core/getters/getContext';
import { getFeatures } from '@/redux/core/getters/getFeatures';
import { storeDesignAction } from '@/redux/designs/actions/storeDesignAction';
import { toggleDesignFavoriteAction } from '@/redux/designs/actions/toggleDesignFavoriteAction';
import { getDesignFavorites } from '@/redux/designs/getters/getDesignFavorites';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getPrintAreaForNewConfiguration } from '@/redux/draft/getters/getPrintAreaForNewConfiguration';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { selectIsAdultDesignsVerboten } from '@/redux/productTypes/getters/selectIsAdultDesignsVerboten';
import { StoreDispatch } from '@/redux/types';
import { Button } from '@/components/common/Button';
import { TagInput } from '@/components/common/TagInput';
import { Star } from '@/components/common/icons/Star';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalHeaderBack,
  ModalTitle,
  ModalTypes,
} from '@/components/common/modal/modal';
import { DesignList } from './DesignList';
import { DesignPreview } from './DesignPreview';
import { FavoriteList } from './FavoriteList';
import { FilterToggles } from './FilterToggles';
import { HotTopics } from './HotTopics';
import { RelatedTags } from './RelatedTags';
import { ShopCollectionList } from './ShopCollectionList';
import { useDesigns } from './hooks/useDesigns';
import './DesignSelector.scss';

export const LIST_ROUTE = '/designs';
const FILTER_ROUTE = `${LIST_ROUTE}/filter`;
const FAVORITES_ROUTE = `${LIST_ROUTE}/favorites`;

export const addSearchTermToRoute = (route: string, searchTermParam: string) =>
  `${route}?searchTerm=${encodeURIComponent(searchTermParam)}`;

export type FilterState = {
  safeSearch: boolean;
  showOnlyFreeDesigns: boolean;
  vectorDesigns: boolean;
};

export enum DesignSelectorTab {
  User,
  Shop,
  Community,
}

const DesignSelectorTabTranslationKeys = [
  'designSelector.myDesigns',
  'designSelector.thisShop',
  'designSelector.community',
];

export const DesignSelector: FunctionComponent = () => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch({ path: LIST_ROUTE, end: false }));
  const isFilterPanelActive = Boolean(useMatch(FILTER_ROUTE));
  const isFavoritePanelActive = Boolean(useMatch(FAVORITES_ROUTE));
  const selectedDesignMatch = useMatch(`${LIST_ROUTE}/:id`);
  const selectedDesignId =
    !!selectedDesignMatch && !isFilterPanelActive && !isFavoritePanelActive
      ? selectedDesignMatch.params.id
      : undefined;

  const [searchParams] = useSearchParams();
  const urlSearchTerm = searchParams.get('searchTerm') || '';
  const urlIsFreeEnabled = searchParams.get('isFreeEnabled') || false;
  const cachedSearchTerm = useRef(urlSearchTerm);
  const isFirstRenderAfterActive = useRef(true);
  const searchTerm =
    isFirstRenderAfterActive.current || !isActive ? cachedSearchTerm.current : urlSearchTerm;
  const hasSearchTerm = Boolean(searchTerm);

  const withSearchTerm = (route: string, searchTermParam = searchTerm) =>
    searchTermParam ? addSearchTermToRoute(route, searchTermParam) : route;
  const navigateWithSearchTerm = (route: string, searchTermParam?: string, replace?: boolean) =>
    navigate(withSearchTerm(route, searchTermParam), { replace });

  const dispatch = useDispatch<StoreDispatch>();
  const features = useSelector(getFeatures);
  const context = useSelector(getContext);
  const printArea = useSelector(getPrintAreaForNewConfiguration);
  const productType = useSelector(getProductType);
  const appearance = useSelector(getCurrentAppearance);
  const favoritedDesignIds = useSelector(getDesignFavorites);
  const adultDesignsVerboten = useSelector(selectIsAdultDesignsVerboten);

  const availablePrintTypes = printArea ? getPrintTypesForPrintArea(printArea, appearance) : [];
  const supportsOnlyVectorDesigns =
    availablePrintTypes.every(isPrintColorColorSpace) ||
    getHasScreenPrintingPrintType(availablePrintTypes);

  const tabs = useMemo(() => {
    if (isCYOMode()) {
      return [DesignSelectorTab.Community];
    }

    if (isUserAreaMode()) {
      return [DesignSelectorTab.User, DesignSelectorTab.Community];
    }

    if (features.showMarketplaceDesigns) {
      return [DesignSelectorTab.Shop, DesignSelectorTab.Community];
    }

    return [DesignSelectorTab.Shop];
  }, [features]);

  const isSmall = useIsSmall();
  const limit = isEmbroideryMode() ? 100 : isSmall ? 50 : 100;

  const [selectedTab, setSelectedTab] = useState<DesignSelectorTab>(tabs[0]);
  const [topics, setTopics] = useState<HotTopic[]>([]);
  const [collections, setCollections] = useState<ShopCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<ShopCollection>();
  const [filterState, setFilterState] = useState<FilterState>({
    safeSearch: true,
    showOnlyFreeDesigns: urlIsFreeEnabled === 'true',
    vectorDesigns: supportsOnlyVectorDesigns,
  });

  const isCommunityTab = selectedTab === DesignSelectorTab.Community;
  const isCommunitySelection = isCommunityTab && !hasSearchTerm;
  const isCommunitySearchResult = isCommunityTab && hasSearchTerm;
  const isShopTab = selectedTab === DesignSelectorTab.Shop;
  const isDesignListVisible = isEmbroideryMode() ? true : !(isSmall && isCommunitySelection);
  const hasFilters = isCommunitySearchResult || isShopTab;
  const isAsideFiltersVisible = !isSmall && hasFilters;
  const isSmallFilterToggleVisible = isSmall && hasFilters;

  // update the safeSearch filter if the user switches to a product that only allows safe designs
  useEffect(() => {
    if (adultDesignsVerboten) {
      setFilterState((currentFilterState) => ({
        ...currentFilterState,
        safeSearch: adultDesignsVerboten,
      }));
    }
  }, [adultDesignsVerboten]);

  // update the vectorDesigns filter if the print type support changes
  useEffect(() => {
    setFilterState((currentFilterState) => ({
      ...currentFilterState,
      vectorDesigns: supportsOnlyVectorDesigns,
    }));
  }, [supportsOnlyVectorDesigns]);

  const toggleDesignFavorite = useCallback(
    ({ id: designId }: Design, isFavorited: boolean) => {
      if (!isFavorited) {
        trackFeature('design-favorite-add', { designId }, true);
      }
      dispatch(toggleDesignFavoriteAction(designId));
    },
    [dispatch]
  );

  useEffect(() => {
    if (tabs.includes(DesignSelectorTab.Community)) {
      fetchHotTopics().then(setTopics);
    }

    if (tabs.includes(DesignSelectorTab.Shop)) {
      fetchShopCollections().then((r) => setCollections(r.results));
    }
  }, [tabs]);

  useEffect(() => {
    if (isActive) {
      // set last search term when reopening the panel
      if (!urlSearchTerm && hasSearchTerm) {
        navigateWithSearchTerm(LIST_ROUTE, searchTerm, true);
      }
    }

    isFirstRenderAfterActive.current = !isActive;

    // only a change to the active state should trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    cachedSearchTerm.current = searchTerm;
    // only a change to the searchTerm should trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // close favorite panel when last item is removed
  useEffect(() => {
    if (isFavoritePanelActive && !favoritedDesignIds.length) {
      navigateWithSearchTerm(LIST_ROUTE, searchTerm, true);
    }
    // only changes to the favoritedDesignIds should trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritedDesignIds.length, isFavoritePanelActive]);

  const isInitialFetch = useRef(true);
  const [
    loading,
    designCount,
    designs,
    scrollKey,
    scrollStartIndex,
    printabilityMap,
    loadMoreItems,
    isBlocklisted,
  ] = useDesigns(
    isActive,
    searchTerm,
    filterState,
    limit,
    selectedTab,
    selectedCollection,
    printArea,
    appearance
  );

  useEffect(() => {
    if (isInitialFetch.current && designCount === -1) {
      // wait for the first response to arrive
      return;
    }

    if (
      isInitialFetch.current &&
      designCount === 0 &&
      selectedTab === DesignSelectorTab.Shop &&
      features.showMarketplaceDesigns
    ) {
      // if the initial fetch of the shop is empty
      //  switch to the MP designs if available
      setSelectedTab(DesignSelectorTab.Community);
    }

    isInitialFetch.current = false;
  }, [designCount, selectedTab, features]);

  function addDesign(design: Design) {
    trackFeature(
      'design-selection-select',
      {
        designId: design.id,
        compositionId: design.ideaId,
        compositionName: design.name,
        compositionPrice: design.free
          ? 0
          : 'designBasedCommission' in context.priceStrategy
          ? context.priceStrategy?.designBasedCommission?.amount
          : 0,
      },
      true
    );
    dispatch(addDesignConfigurationAction(design.id));
    handleClose();
  }

  function selectDesign(design: Design) {
    const designId = design.id;
    const position = designs.indexOf(design);
    if (isCYOMode()) {
      trackWellness('designSelection', {
        designId,
        position,
        search: searchTerm,
        compositionId: design.ideaId,
      });
    }
    dispatch(storeDesignAction(design));
    if (isSmall) {
      navigateWithSearchTerm(`${LIST_ROUTE}/${designId}`);
    } else {
      addDesign(design);
    }
  }

  function handleBack() {
    navigateWithSearchTerm(LIST_ROUTE);
  }

  function handleClose() {
    navigate('/');
  }

  const title = isEmbroideryMode() ? t('designPanel.title.embroidery') : t('designPanel.title');
  const favoritePanelTitle = t('designSelector.favoriteListHeading');
  const filterPanelTitle = t('productTypePanel.changeFilter');
  const favoritePanelToggleClasses = cn('design-selector__favorite-toggle', {
    'design-selector__favorite-toggle--active': isFavoritePanelActive,
    'design-selector__favorite-toggle--disabled': !favoritedDesignIds.length,
  });

  let filterContent = null;
  if (isCommunitySearchResult) {
    filterContent = (
      <FilterToggles
        filterState={filterState}
        onFilterChanged={(filter: Partial<FilterState>) => {
          setFilterState({ ...filterState, ...filter });
        }}
        safeSearchToggleEnabled={!adultDesignsVerboten}
        supportsOnlyVectorDesigns={supportsOnlyVectorDesigns}
      />
    );
  } else if (isShopTab) {
    filterContent = (
      <ShopCollectionList
        collections={collections}
        selectedCollection={selectedCollection}
        onCollectionClick={setSelectedCollection}
      />
    );
  }

  return (
    <Fragment>
      <Modal
        type={ModalTypes.panel}
        open={isActive}
        title={title}
        onClose={handleClose}
        className={isEmbroideryMode() ? 'embroideryGalleryModal' : ''}
      >
        <ModalHeader>
          {isSmall && isCommunitySearchResult && (
            <ModalHeaderBack onBack={handleBack}>{t('modal.back', title)}</ModalHeaderBack>
          )}
          <ModalTitle>{title}</ModalTitle>
          <Link
            to={withSearchTerm(isFavoritePanelActive ? LIST_ROUTE : FAVORITES_ROUTE)}
            className={favoritePanelToggleClasses}
            title={t('favorites.view')}
            onClick={() => trackFeature('design-favorite-panel-open')}
          >
            <span className="design-selector__favorite-label">{favoritePanelTitle}</span>
            <Star width="1.75em" height="1.75em" />
            <span
              className={cn('design-selector__favorite-toggle-badge', {
                'design-selector__favorite-toggle-badge--hidden': !favoritedDesignIds.length,
              })}
            >
              {favoritedDesignIds.length}
            </span>
          </Link>
        </ModalHeader>
        <ModalContent>
          <div className="design-selector__wrapper">
            {isEmbroideryMode() ? null : (
              <div className="design-filter">
                <div className="design-filter__content">
                  {tabs.length > 1 && (
                    <div className="design-filter__categories">
                      {tabs.map((tab) => {
                        return (
                          <button
                            type="button"
                            key={tab}
                            className={cn('design-filter__button', {
                              'design-filter__button--selected': tab === selectedTab,
                            })}
                            onClick={() => setSelectedTab(tab)}
                          >
                            {t(DesignSelectorTabTranslationKeys[tab])}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <TagInput
                    key={searchTerm}
                    inputValue={searchTerm}
                    onSelect={(searchTermParam: string) =>
                      navigateWithSearchTerm(LIST_ROUTE, encodeURIComponent(searchTermParam))
                    }
                  />
                  {isAsideFiltersVisible && filterContent}
                  {isCommunityTab && isAsideFiltersVisible && (
                    <RelatedTags searchTerm={searchTerm} />
                  )}
                  {isCommunitySelection && <HotTopics topics={topics} />}
                </div>
              </div>
            )}
            {isDesignListVisible && (
              <DesignList
                loading={loading}
                isSmall={isSmall}
                designs={designs}
                designCount={designCount}
                limit={limit}
                selectedTab={selectedTab}
                productType={productType}
                appearance={appearance}
                printabilityMap={printabilityMap}
                scrollKey={scrollKey}
                scrollStartIndex={scrollStartIndex}
                context={context}
                selectedDesignId={selectedDesignId}
                favoritedDesignIds={favoritedDesignIds}
                showDesignPrices={features.showDesignPrices}
                selectDesign={selectDesign}
                toggleDesignFavorite={toggleDesignFavorite}
                topics={topics}
                loadMoreItems={loadMoreItems}
                filterUnprintableDesigns={isCommunitySearchResult && filterState.vectorDesigns}
                isBlocklisted={isBlocklisted}
              />
            )}
            {isSmallFilterToggleVisible && (
              <div className="design-selector__cta-container">
                <Button
                  className="design-selector__cta"
                  medium
                  onClick={() => navigateWithSearchTerm(FILTER_ROUTE)}
                >
                  {t('productTypePanel.changeFilter')}
                </Button>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
      <DesignPreview
        designId={selectedDesignId}
        onBack={handleBack}
        onClose={handleClose}
        appearance={appearance}
        printArea={printArea}
        context={context}
        onDesignSelect={addDesign}
        isMarketplaceCategoryActive={isCommunityTab}
        productType={productType}
      />
      <Modal
        type={ModalTypes.panel}
        open={isFilterPanelActive}
        title={filterPanelTitle}
        onClose={handleClose}
      >
        <ModalHeader>
          <ModalHeaderBack onBack={handleBack}>{t('modal.back', title)}</ModalHeaderBack>
          <ModalTitle>{filterPanelTitle}</ModalTitle>
        </ModalHeader>
        <ModalContent flex>
          <div className="design-filter__panel-content">{filterContent}</div>
          <div className="design-selector__cta-container">
            <Button medium className="design-selector__cta" onClick={handleBack}>
              {t('productTypePanel.acceptFilter')}
            </Button>
          </div>
        </ModalContent>
      </Modal>
      <Modal
        type={ModalTypes.panel}
        open={isFavoritePanelActive}
        title={favoritePanelTitle}
        onClose={handleClose}
      >
        <ModalHeader>
          <ModalHeaderBack onBack={handleBack}>{t('modal.back', title)}</ModalHeaderBack>
          <ModalTitle>{favoritePanelTitle}</ModalTitle>
        </ModalHeader>
        <ModalContent flex>
          <FavoriteList
            selectDesign={(design) => {
              trackFeature('design-favorite-select');
              addDesign(design);
            }}
            toggleDesignFavorite={toggleDesignFavorite}
            isSmall={isSmall}
          />
        </ModalContent>
      </Modal>
    </Fragment>
  );
};
